import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../../api";
import ResponsiveTable from "../../components/ResponsiveTable";

// Palette mirrors pages/Admin/CourseWaitingList.jsx so the enquiry inboxes read as one console.
const PAGE_BACKGROUND = "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)";
const HEADER_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const CARD_BACKGROUND = "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)";
const ROW_HOVER = "rgba(102, 126, 234, 0.05)";

const STATUS_OPTIONS = ["new", "shortlisted", "interviewed", "hired", "rejected"];
const ROLE_LABELS = { tutor: "Tutor", teaching_assistant: "Teaching Assistant", centre_manager: "Centre Manager", invigilator: "Invigilator" };
const STATUS_COLORS = { new: "warning", shortlisted: "info", interviewed: "secondary", hired: "success", rejected: "default" };
const MODE_LABELS = { online: "Online", in_person: "In person", both: "Online & in person" };
const DBS_LABELS = { yes: "Yes", in_progress: "In progress", no: "No" };
const EXPERIENCE_LABELS = { 0: "< 1 year", 1: "1–2 years", 3: "3–4 years", 5: "5–9 years", 10: "10+ years" };

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const joinOrDash = (list) => (Array.isArray(list) && list.length ? list.join(", ") : "—");

// Centre managers are not asked about teaching (see TutorApplication::NON_TEACHING_ROLES).
const experienceLabel = (entry) => {
  if (entry.role === "centre_manager" && entry.years_experience == null) return "Not asked";
  return EXPERIENCE_LABELS[entry.years_experience] ?? (entry.years_experience != null ? `${entry.years_experience} years` : "—");
};
const yesNo = (value) => (value == null ? "—" : value ? "Yes" : "No");

const TutorApplications = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("");
  const [role, setRole] = useState("");
  const [teachingMode, setTeachingMode] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("admin/tutor-applications", {
        params: {
          page: page + 1,
          per_page: rowsPerPage,
          search: searchTerm || undefined,
          status: status || undefined,
          role: role || undefined,
          teaching_mode: teachingMode || undefined,
        },
      });
      setEntries(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotalRecords(res.data?.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch applications.");
      setEntries([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, status, role, teachingMode]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const updateEntry = async (entry, changes) => {
    const res = await api.patch(`admin/tutor-applications/${entry.id}`, changes);
    const updated = { ...entry, ...(res.data?.data || changes) };
    setEntries((prev) => prev.map((row) => (row.id === entry.id ? { ...row, ...updated } : row)));
    if (selected?.id === entry.id) setSelected((prev) => ({ ...prev, ...updated }));
    return updated;
  };

  const handleStatusChange = async (entry, newStatus) => {
    try {
      await updateEntry(entry, { status: newStatus });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update the status.");
    }
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await updateEntry(selected, { admin_notes: notes });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save notes.");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDownloadCv = async (entry) => {
    setDownloadingId(entry.id);
    try {
      const res = await api.get(`admin/tutor-applications/${entry.id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", entry.cv_original_name || `tutor-cv-${entry.id}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Could not download the CV.");
    } finally {
      setDownloadingId(null);
    }
  };

  const openDetails = (entry) => {
    setSelected(entry);
    setNotes(entry.admin_notes || "");
  };

  const resetToFirstPage = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  const detailRows = selected
    ? [
        ["Role", ROLE_LABELS[selected.role] || "Tutor"],
        ["Email", selected.email],
        ["Mobile", selected.phone],
        ["Subjects", joinOrDash(selected.subject_names)],
        ["Year groups", joinOrDash(selected.year_group_names)],
        ["Teaching mode", MODE_LABELS[selected.teaching_mode] || "—"],
        ["Locations", joinOrDash(selected.location_names)],
        ["Available days", joinOrDash(selected.available_days)],
        ["Experience", experienceLabel(selected)],
        ["Highest qualification", selected.highest_qualification || "—"],
        ["11+ experience", selected.has_11plus_experience ? "Yes" : "No"],
        ["Enhanced DBS", DBS_LABELS[selected.dbs_status] || "—"],
        ["Right to work in UK", yesNo(selected.right_to_work_uk)],
        ["CV file", selected.cv_original_name || "—"],
        ["Submitted", formatDate(selected.created_at)],
      ]
    : [];

  return (
    <Box sx={{ minHeight: "100vh", background: PAGE_BACKGROUND, p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 1 }}>
          Tutor Applications
        </Typography>
        <Typography variant="body2" sx={{ color: "#5a6c7d", fontSize: "13px" }}>
          Applications from Contact Us → Careers (tutors, teaching assistants, centre managers, invigilators) · Total - {totalRecords}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, background: CARD_BACKGROUND, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Search name, email or mobile" value={searchTerm} onChange={resetToFirstPage(setSearchTerm)} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={role} onChange={resetToFirstPage(setRole)}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(ROLE_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={resetToFirstPage(setStatus)}>
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Teaching mode</InputLabel>
              <Select label="Teaching mode" value={teachingMode} onChange={resetToFirstPage(setTeachingMode)}>
                <MenuItem value="">All</MenuItem>
                {Object.entries(MODE_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ background: CARD_BACKGROUND, borderRadius: 2, overflow: "hidden" }}>
        <ResponsiveTable>
          <Table>
            <TableHead sx={{ "& .MuiTableCell-head": { background: HEADER_GRADIENT, color: "white", fontWeight: 600, whiteSpace: "nowrap" } }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Subjects</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress size={28} /></TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">No applications found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow
                    key={entry.id}
                    hover
                    onClick={() => openDetails(entry)}
                    sx={{ cursor: "pointer", "&:hover": { backgroundColor: ROW_HOVER } }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>{entry.full_name || `${entry.first_name} ${entry.last_name}`}</TableCell>
                    <TableCell>{ROLE_LABELS[entry.role] || "Tutor"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{entry.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{entry.phone || "—"}</Typography>
                    </TableCell>
                    <TableCell>{entry.role === "centre_manager" ? "—" : joinOrDash(entry.subject_names)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(entry.created_at)}</TableCell>
                    {/* Status and CV controls must not open the details dialog (portal clicks bubble to the row). */}
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Select
                        size="small"
                        value={entry.status}
                        onChange={(e) => handleStatusChange(entry, e.target.value)}
                        renderValue={(value) => <Chip size="small" label={value} color={STATUS_COLORS[value] || "default"} />}
                        sx={{ minWidth: 140 }}
                      >
                        {STATUS_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      <Button size="small" onClick={() => openDetails(entry)} sx={{ textTransform: "none", mr: 1 }}>
                        View
                      </Button>
                      <Button
                        size="small"
                        startIcon={downloadingId === entry.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                        onClick={() => handleDownloadCv(entry)}
                        disabled={downloadingId === entry.id}
                        sx={{ textTransform: "none" }}
                      >
                        CV
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ResponsiveTable>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>{selected?.full_name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {detailRows.map(([label, value]) => (
              <Box key={label} sx={{ display: "flex", gap: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", width: 170, flexShrink: 0 }}>{label}</Typography>
                <Typography variant="body2">{value || "—"}</Typography>
              </Box>
            ))}
            <Box>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, mb: 0.5 }}>Cover note</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", bgcolor: "#f9fafb", p: 1.5, borderRadius: 1 }}>
                {selected?.message || "—"}
              </Typography>
            </Box>
            <TextField label="Admin notes" multiline minRows={3} value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mt: 1 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadIcon />} onClick={() => selected && handleDownloadCv(selected)} sx={{ textTransform: "none", mr: "auto" }}>
            Download CV
          </Button>
          <Button onClick={() => setSelected(null)} sx={{ textTransform: "none" }}>Close</Button>
          <Button variant="contained" onClick={handleSaveNotes} disabled={savingNotes} sx={{ textTransform: "none" }}>
            {savingNotes ? "Saving…" : "Save notes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TutorApplications;
